/**
 * Salvataggio delle foto di un prodotto per differenza (puro). Prima si cancellavano tutte le righe di
 * product_images e si reinserivano: ogni salvataggio cambiava l'identita' di ogni foto e il motore dei
 * negozi le ricaricava tutte su Shopify/WooCommerce (e staccava le vecchie). Ora una foto gia' salvata
 * tiene la sua riga: si aggiornano solo testo alternativo, principale e posizione se cambiati.
 * Abbinamento: per id della riga; se manca (foto appena caricata), per indirizzo del file.
 * La posizione e' l'ordine in cui arrivano dal modulo (la prima e' in cima alla galleria).
 */

export interface SavedImage {
  id: string;
  file_path: string;
  alt_text: string | null;
  is_main: boolean | null;
  position: number | null;
}

export interface FormImage {
  id?: string;
  url: string;
  alt_text: string;
  is_main: boolean;
}

export interface ImageRowValues {
  file_path: string;
  filename: string;
  alt_text: string;
  is_main: boolean;
  position: number;
}

export interface ImageSyncPlan {
  remove: string[];
  update: { id: string; values: Pick<ImageRowValues, 'alt_text' | 'is_main' | 'position'> }[];
  insert: ImageRowValues[];
}

const filenameOf = (url: string) => url.split('/').pop() || 'image';

export function planImageSync(saved: readonly SavedImage[], desired: readonly FormImage[]): ImageSyncPlan {
  const byId = new Map(saved.map((s) => [s.id, s]));
  const byPath = new Map(saved.map((s) => [s.file_path, s]));
  const kept = new Set<string>();
  const plan: ImageSyncPlan = { remove: [], update: [], insert: [] };
  desired.forEach((d, position) => {
    const values = { alt_text: d.alt_text || '', is_main: !!d.is_main, position };
    const cur = (d.id && byId.get(d.id)) || byPath.get(d.url);
    if (cur && !kept.has(cur.id)) {
      kept.add(cur.id);
      const same = (cur.alt_text ?? '') === values.alt_text && !!cur.is_main === values.is_main && (cur.position ?? 0) === position;
      if (!same) plan.update.push({ id: cur.id, values });
      return;
    }
    plan.insert.push({ file_path: d.url, filename: filenameOf(d.url), ...values });
  });
  plan.remove = saved.filter((s) => !kept.has(s.id)).map((s) => s.id);
  return plan;
}
